import CardActionArea from '@material-ui/core/CardActionArea';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import clsx from 'clsx';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ACCORDION_VIEW_APPOINTMENT_SELECTION_LIMIT } from '@/utils/constants.js';
import { useQuery } from '@/utils/helpers.js';

import {
    CLEARED_CHAIN_SELECTION,
    selectChainConfig,
    useConfigStore,
    usePersistedPrismicStore,
    useSearchStore,
} from '@/store.js';

import { OnlinePill } from '../Common/online-pill.tsx';
import { ProfilePicturePlaceholder } from '../Common/profile-picture-placeholder.tsx';

const useStyles = makeStyles((theme) => ({
    card: {
        borderRadius: theme.radius.default,
        border: `1px solid ${theme.palette.common.yellow}`,
        padding: '16px',
        userSelect: 'none',
        cursor: 'pointer',
        whiteSpace: 'pre-wrap',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        transition: 'background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        textOverflow: 'ellipsis',
        boxSizing: 'border-box',
        '&:hover': {
            background: '#f3f3f3',
            borderColor: theme.palette.common.hoverYellow,
        },
        '&:active': {
            background: '#eaeaea',
            borderColor: theme.palette.common.hoverYellow,
        },
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    treatmentCard: {
        gap: 0,
        width: '15rem',
        flexDirection: 'column',
        alignItems: 'start',
    },
    accordionCard: {
        // A minimum, not a fixed height: a wrapping treatment name has to grow the card rather
        // than overflow into the description below it.
        minHeight: '9rem',
    },
    chainCard: {
        // CardActionArea owns the inner padding; strip the wrapper's to avoid double-padding.
        padding: 0,
    },
    chainCardActionArea: {
        height: '100%',
        width: '100%',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    treatmentCardTitleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        gap: '0.5rem',
        alignItems: 'start',
    },
    selectedAccordionCard: {
        borderColor: theme.palette.common.yellow,
        boxShadow: `inset 0 0 0 2px ${theme.palette.common.yellow}`,
    },
    treatmentDetailsWrapper: {
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        marginTop: '0.25rem',
    },
    treatmentDetails: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: '12rem',
        width: '100%',
        fontWeight: 700,
    },
    treatmentDescription: {
        margin: 0,
        flexShrink: 1,
        flexGrow: 1,
        color: '#686868',
        fontSize: '1rem',
        marginBottom: '0.25rem',
    },
    professionalProfilePicture: {
        width: '3rem',
        height: '3rem',
        borderRadius: '50%',
        objectFit: 'cover',
    },
    professionalCardInfoContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
        // Takes the free space so every name starts at the same x. Without this a short name is
        // pushed toward the middle by space-between while a long one wraps against the left.
        flex: 1,
        minWidth: 0,
        textAlign: 'left',
    },
    infoText: {
        color: 'rgba(46, 46, 46, 0.70)',
        fontWeight: 350,
        fontSize: '14px',
    },
    treatmentRow: {
        // No fixed width: the row fills its grid column so every card in a row matches, and a
        // single height keeps the list even regardless of how long a name is.
        appearance: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        width: '100%',
        boxSizing: 'border-box',
        minHeight: '3.25rem',
        padding: '0.75rem 1rem',
        backgroundColor: theme.palette.common.white,
        border: theme.border.light,
        borderRadius: theme.radius.field,
        cursor: 'pointer',
        transition: 'border-color 200ms ease',
        '&:hover': {
            borderColor: theme.palette.common.yellow,
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.common.darkGrey}`,
            outlineOffset: 2,
        },
    },
    treatmentRowSelected: {
        borderColor: theme.palette.common.yellow,
        backgroundColor: theme.palette.common.lightBrown,
    },
    treatmentRowTitle: {
        minWidth: 0,
        overflowWrap: 'anywhere',
    },
    treatmentRowMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexShrink: 0,
    },
    treatmentRowChevron: {
        fontSize: '1.25rem',
        color: theme.palette.common.darkGrey,
    },
    seeMoreButton: {
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        color: theme.palette.common.yellow,
        textTransform: 'none',
        marginLeft: '0.25rem',
        fontWeight: 'bold',
    },
}));

export const SearchResultType = {
    TREATMENT: 'treatment',
    SPECIALTY: 'specialty',
    PROFESSIONAL: 'professional',
    CHAIN: 'chain',
};

const SearchResultCard = ({ searchResultType = SearchResultType.TREATMENT, prismicData, cardKey, config }) => {
    const classes = useStyles();
    const history = useHistory();
    const query = useQuery();
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [professionalData, setProfessionalData] = useState(null);
    const search = useSearchStore((state) => state.search);
    const locations = useSearchStore((state) => state.locations);
    const treatments = useSearchStore((state) => state.treatments);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const selectedTreatments = useSearchStore((state) => state.selectedTreatments);
    const selectedChainConfig = useSearchStore((state) => state.selectedChainConfig);
    const isOnlineAppointment = useSearchStore((state) => state.isOnlineAppointment);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);
    const {
        [i18n.language]: { professionalProfiles: processedProfessionalProfiles },
    } = usePersistedPrismicStore((state) => state.findAppointmentData);
    const ref = useRef();

    useEffect(() => {
        if (searchResultType !== SearchResultType.PROFESSIONAL) return;

        const professional = processedProfessionalProfiles.find(
            (professional) => professional.key[0].text === prismicData.key[0].text
        );

        setProfessionalData(professional);
    }, [prismicData, searchResultType]);

    const onSpecialtySelected = () => {
        query.set('specialty', prismicData.key[0].text);
        history.replace({ search: query.toString() });
        useSearchStore.setState({ selectedSpecialty: prismicData.key[0].text, search: prismicData.name[0].text });
    };

    const onProfessionalSelected = () => {
        query.set('selected_professional', prismicData.key[0].text);
        history.replace({ search: query.toString() });
        useSearchStore.setState({
            selectedProfessional: prismicData.key[0].text,
            search: prismicData.display_name[0].text,
        });
    };

    const getUnderlinedText = (text) => {
        // Guard on the trimmed search: a whitespace-only search would otherwise build a /()/gi regex
        // and split the text per character, inflating the DOM.
        const trimmedSearch = search.trim();
        if (!trimmedSearch) {
            return (
                <span
                    lang={i18n.language}
                    style={{
                        display: 'block',
                        hyphens: 'auto',
                    }}
                >
                    {text}
                </span>
            );
        }

        // Use a regular expression to split the text in a case-insensitive manner, while preserving the original case.
        // Escape special characters in the search string to ensure the regular expression works correctly.
        const escapedSearch = trimmedSearch.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        const regex = new RegExp(`(${escapedSearch})`, 'gi');

        // Split the text into parts, including the search text.
        const parts = text.split(regex);

        return (
            <span
                lang={i18n.language}
                style={{
                    display: 'block',
                    hyphens: 'auto',
                }}
            >
                {parts.map((part, index) => {
                    // Check if the current part matches the search text, using a case-insensitive comparison.
                    const isMatch = part.toLowerCase() === trimmedSearch.toLowerCase();
                    return (
                        <Fragment key={index}>
                            {!isMatch ? (
                                // This is a non-search part of the text.
                                <span
                                    lang={i18n.language}
                                    style={{
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {part}
                                </span>
                            ) : (
                                // This part matches the search text and should be underlined.
                                <span
                                    lang={i18n.language}
                                    style={{
                                        textDecoration: 'underline',
                                        fontWeight: theme.weight.heading,
                                    }}
                                >
                                    {part}
                                </span>
                            )}
                        </Fragment>
                    );
                })}
            </span>
        );
    };

    const isDirectSelectAccordion =
        currentCustomer?.is_accordion_view_active &&
        !currentCustomer?.is_category_view_active &&
        !currentCustomer?.is_multi_treatment_booking_allowed;

    const updateSelectedTreatments =
        (isOnline = false) =>
        (event) => {
            if (event.target.id === `${cardKey}_see_more`) return;
            if (currentCustomer?.is_accordion_view_active && !isDirectSelectAccordion) {
                let newSelectedTreatments = { ...selectedTreatments };

                if (!currentCustomer?.is_multi_treatment_booking_allowed) {
                    newSelectedTreatments = {
                        [prismicData.key[0].text]: true,
                    };
                } else if (newSelectedTreatments[prismicData.key[0].text] && isOnline === isOnlineAppointment) {
                    delete newSelectedTreatments[prismicData.key[0].text];
                } else if (
                    Object.keys(newSelectedTreatments).length === 0 ||
                    (!isOnlineAppointment &&
                        !isOnline &&
                        Object.keys(newSelectedTreatments).length < ACCORDION_VIEW_APPOINTMENT_SELECTION_LIMIT)
                ) {
                    newSelectedTreatments[prismicData.key[0].text] = true;
                } else {
                    return;
                }

                useSearchStore.setState({
                    selectedTreatments: newSelectedTreatments,
                    ...CLEARED_CHAIN_SELECTION,
                    isOnlineAppointment: isOnline,
                });

                return;
            }

            useSearchStore.setState({
                selectedTreatments: {
                    [prismicData.key[0].text]: true,
                },
                ...CLEARED_CHAIN_SELECTION,
                showAvailableProfessionals: true,
                isOnlineAppointment: isOnline,
            });
        };

    const updateSelectedChain = () => selectChainConfig(config);

    if (searchResultType === SearchResultType.CHAIN) {
        const chainName = config.name[i18n.language] ?? config.name.de;
        const stepCount = config.steps.length;
        const stepBadge = t('ChainStepCountBadge', { count: stepCount });
        const isSelected = selectedChainConfig?.id === config.id;

        return (
            <div
                className={clsx(
                    classes.card,
                    classes.treatmentCard,
                    classes.chainCard,
                    isSelected && classes.selectedAccordionCard
                )}
                style={{ width: '100%' }}
            >
                <CardActionArea
                    data-testid={`search-chain-card-${config.id}`}
                    aria-pressed={isSelected}
                    onClick={updateSelectedChain}
                    className={classes.chainCardActionArea}
                >
                    <span className={classes.treatmentCardTitleContainer}>{getUnderlinedText(chainName)}</span>
                    <span className={classes.treatmentDetailsWrapper}>
                        <span className={classes.treatmentDetails}>
                            <Typography component="span" style={{ margin: 0 }}>
                                {stepBadge}
                            </Typography>
                        </span>
                    </span>
                </CardActionArea>
            </div>
        );
    }

    if (searchResultType === SearchResultType.TREATMENT) {
        const locationTreatment =
            treatments[selectedLocation ?? currentCustomer.locations[0].key[0].text]?.[prismicData.key[0].text];

        const renderRow = (isOnline) => {
            const isSelected = Boolean(selectedTreatments[prismicData.key[0].text]) && isOnlineAppointment === isOnline;

            return (
                <button
                    type="button"
                    key={`${cardKey}-${isOnline ? 'online' : 'location'}`}
                    data-testid={`search-treatment-card-${cardKey}${isOnline ? '-online' : ''}`}
                    aria-pressed={isSelected}
                    className={clsx(classes.treatmentRow, isSelected && classes.treatmentRowSelected)}
                    onClick={(e) => updateSelectedTreatments(isOnline)(e)}
                    ref={ref}
                >
                    <span className={classes.treatmentRowTitle}>{getUnderlinedText(prismicData.name[0].text)}</span>
                    <span className={classes.treatmentRowMeta}>
                        {isOnline && <OnlinePill />}
                        <ChevronRightIcon className={classes.treatmentRowChevron} />
                    </span>
                </button>
            );
        };

        return (
            <>
                {(prismicData.location || locationTreatment?.location) && renderRow(false)}
                {(prismicData.online || locationTreatment?.online) && renderRow(true)}
            </>
        );
    }

    if (searchResultType === SearchResultType.SPECIALTY) {
        return (
            <div
                key={cardKey}
                className={classes.card}
                onClick={onSpecialtySelected}
                data-testid={`search-specialty-card-${cardKey}`}
            >
                {getUnderlinedText(prismicData.name[0].text)}
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M10 19C9.71875 19 9.46875 18.9062 9.28125 18.7188C8.875 18.3438 8.875 17.6875 9.28125 17.3125L14.5625 12L9.28125 6.71875C8.875 6.34375 8.875 5.6875 9.28125 5.3125C9.65625 4.90625 10.3125 4.90625 10.6875 5.3125L16.6875 11.3125C17.0938 11.6875 17.0938 12.3438 16.6875 12.7188L10.6875 18.7188C10.5 18.9062 10.25 19 10 19Z"
                        fill="#2E2E2E"
                    />
                </svg>
            </div>
        );
    }

    if (searchResultType === SearchResultType.PROFESSIONAL) {
        return (
            <div
                key={cardKey}
                className={classes.card}
                onClick={onProfessionalSelected}
                data-testid={`search-professional-card-${cardKey}`}
            >
                {prismicData.profile_picture?.url ? (
                    <img
                        className={classes.professionalProfilePicture}
                        src={prismicData.profile_picture.url}
                        alt="professional profile"
                    />
                ) : (
                    <ProfilePicturePlaceholder className={classes.professionalProfilePicture} />
                )}
                <div className={classes.professionalCardInfoContainer}>
                    {getUnderlinedText(prismicData.display_name[0].text)}
                    <Typography className={classes.infoText}>
                        {professionalData
                            ? professionalData.specialityType.name[0].text
                            : Object.values(locations).find(
                                  (location) => location.prismicId === prismicData.location_hub.id
                              )?.title?.[0]?.text}
                    </Typography>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M10 19C9.71875 19 9.46875 18.9062 9.28125 18.7188C8.875 18.3438 8.875 17.6875 9.28125 17.3125L14.5625 12L9.28125 6.71875C8.875 6.34375 8.875 5.6875 9.28125 5.3125C9.65625 4.90625 10.3125 4.90625 10.6875 5.3125L16.6875 11.3125C17.0938 11.6875 17.0938 12.3438 16.6875 12.7188L10.6875 18.7188C10.5 18.9062 10.25 19 10 19Z"
                        fill="#2E2E2E"
                    />
                </svg>
            </div>
        );
    }
};

export default SearchResultCard;
